/**
 * ProvinceMetricsPanel — Panel de métricas de completitud (F9.02)
 *
 * Muestra KPIs de cobertura, distribución por tipología y fuentes
 * de geocodificación. Incluye adaptador ProcessedInfrastructure[] → ProvinceMetrics.
 *
 * @version 1.0.0
 * @task F9.02
 */

import React, { useState } from 'react';
import { colors, typography, borderRadius } from '@/design-system/v2';
import type { ProvinceMetrics } from '@/services/pipeline/ProvinceMetricsService';
import type { ProcessedInfrastructure } from '@/types/processing';
import { porcentajeSinEsconder } from '@/utils/porcentajeSinEsconder';
import { InfoBanner, BannerStrong } from '../shell/InfoBanner';

// ============================================================================
// ADAPTER: ProcessedInfrastructure[] → ProvinceMetrics
// ============================================================================

/**
 * Construye un ProvinceMetrics directamente desde la lista de infraestructuras
 * procesadas del wizard (un único municipio).
 */
export function buildMetricsFromInfras(
  infras: ProcessedInfrastructure[],
  provincia: string = 'Sin clasificar',
): ProvinceMetrics {
  const withCoords = infras.filter(i => Boolean(i.xFinal && i.yFinal));

  const cobertura = infras.length > 0
    ? Math.round((withCoords.length / infras.length) * 10000) / 100
    : 0;

  const scores = withCoords.map(i => i.score);
  const precisionMedia = scores.length > 0
    ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length * 100) / 100
    : 0;

  const dist = { alto: 0, medio: 0, bajo: 0, muyBajo: 0 };
  for (const s of scores) {
    if (s >= 80) dist.alto++;
    else if (s >= 50) dist.medio++;
    else if (s >= 20) dist.bajo++;
    else dist.muyBajo++;
  }

  const fuentes: Record<string, number> = {};
  for (const i of withCoords) {
    if (i.source) fuentes[i.source] = (fuentes[i.source] ?? 0) + 1;
  }

  const tipologias: Record<string, { total: number; conCoordenadas: number }> = {};
  for (const i of infras) {
    const t = i.tipo || 'Sin tipo';
    if (!tipologias[t]) tipologias[t] = { total: 0, conCoordenadas: 0 };
    tipologias[t].total++;
    if (i.xFinal && i.yFinal) tipologias[t].conCoordenadas++;
  }

  return {
    provincia,
    municipios: {
      total: 1,
      exitosos: infras.length > 0 ? 1 : 0,
      parciales: 0,
      fallidos: 0,
      omitidos: 0,
    },
    infraestructuras: {
      total: infras.length,
      validadas: withCoords.length,
      geocodificadas: 0,
      sinCoordenadas: infras.length - withCoords.length,
      conErrores: 0,
    },
    cobertura,
    precisionMedia,
    distribucionScores: dist,
    fuentesGeocodificacion: fuentes,
    tipologias,
    tiempoMedioMs: 0,
  };
}

// ============================================================================
// HELPERS DE COLOR
// ============================================================================

/** Color por fuente de geocodificación — coherente con P2 (ExpandableRow) */
function getSourceColor(src: string): { color: string; bg: string; border: string } {
  const s = src.toLowerCase();
  if (s === 'catastro')                     return { color: '#FBBF24', bg: 'rgba(251,191,36,0.10)',  border: 'rgba(251,191,36,0.30)' };
  if (s === 'cartociudad' || s === 'cdau')  return { color: '#60A5FA', bg: 'rgba(96,165,250,0.10)',  border: 'rgba(96,165,250,0.30)' };
  if (s === 'documento')                    return { color: '#A3E635', bg: 'rgba(163,230,53,0.10)',  border: 'rgba(163,230,53,0.30)' };
  return { color: '#94A3B8', bg: 'rgba(148,163,184,0.10)', border: 'rgba(148,163,184,0.30)' };
}

function getCoberturaColor(pct: number): string {
  if (pct >= 80) return colors.scores.high;
  if (pct >= 50) return colors.scores.low;
  return colors.scores.critical;
}

// ============================================================================
// ESTILOS (DS v2: bg.tertiary, borderRadius.xl, typography)
// ============================================================================

const S = {
  root: {
    background: colors.bg.tertiary,
    borderRadius: borderRadius.xl,
    padding: '20px',
    marginTop: '12px',
  },
  title: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    color: colors.fg.secondary,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.4px',
    marginBottom: '16px',
  },
  kpiRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '20px', // más espacio entre KPIs
    marginBottom: '20px',
  },
  kpiCard: {
    background: colors.bg.secondary,
    borderRadius: borderRadius.lg,
    padding: '16px 20px', // más padding interno
    textAlign: 'center' as const,
  },
  kpiValue: {
    fontFamily: typography.fontFamily.mono,
    fontSize: '22px',
    fontWeight: typography.fontWeight.semibold,
    lineHeight: 1.2,
  },
  kpiLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.fg.muted,
    marginTop: '3px',
  },
  sectionTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    color: colors.fg.secondary,
    marginBottom: '10px',
    paddingBottom: '6px',
    borderBottom: `1px solid ${colors.border.DEFAULT}`,
  },
  tipRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '8px',
  },
  tipName: {
    fontSize: typography.fontSize.base,
    color: colors.fg.primary,
    width: '130px',
    flexShrink: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  barTrack: {
    flex: 1,
    height: '8px',
    background: colors.bg.primary,
    borderRadius: '4px',
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.4s ease',
  },
  tipPct: {
    fontFamily: typography.fontFamily.mono,
    fontSize: typography.fontSize.sm,
    color: colors.fg.secondary,
    width: '42px',
    textAlign: 'right' as const,
  },
  divider: {
    borderTop: `1px solid ${colors.border.DEFAULT}`,
    margin: '16px 0',
  },
  sourceRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px',
  },
  // sourcePill sin estilos base — el color se calcula por fuente en el JSX
  sourcePill: {
    borderRadius: '6px',
    padding: '3px 10px',
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.mono,
    fontWeight: typography.fontWeight.semibold,
    border: '1px solid',
  },
} as const;

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

/**
 * P4TARJ: las tres cifras de arriba salen de los mismos datos que la cabecera
 * Antes / Después (filas del plan sin las de plantilla; el centroide municipal
 * no cuenta como geolocalizada), no del recuento propio de `metrics`.
 */
export interface ResumenCabeceraP4 {
  geolocalizadas: number;
  total: number;
  scoreMedio: number;
}

export interface ProvinceMetricsPanelProps {
  metrics: ProvinceMetrics;
  resumen: ResumenCabeceraP4;
  /**
   * VALMUNI-b2b1: texto sellado de `textoSinValidarContorno` cuando el límite del municipio no
   * se pudo cargar; `null`/ausente → sin aviso. Lo resuelve Step3Page, que pide el límite.
   */
  avisoContorno?: string | null;
}

const PREFIJO_AVISO_CONTORNO = 'Sin validar contra el término municipal: ';

export const ProvinceMetricsPanel: React.FC<ProvinceMetricsPanelProps> = ({ metrics, resumen, avisoContorno }) => {
  const {
    fuentesGeocodificacion,
    tipologias,
  } = metrics;
  // INF100: nunca «100%» si falta alguna fila ni «0%» si hay alguna.
  const geoPct = porcentajeSinEsconder(resumen.geolocalizadas, resumen.total);
  const scoreMedio = Math.round(resumen.scoreMedio);

  // Hooks declarados antes de cualquier early return (rules-of-hooks)
  const [open, setOpen] = useState(false);
  const [pulseKey, setPulseKey] = useState(0);

  // Empty state: sin infraestructuras no hay métricas que mostrar
  if (resumen.total === 0) {
    return (
      <div style={S.root} data-testid="province-metrics-panel">
        <div style={S.title}>Métricas de geolocalización</div>
        <div style={{
          textAlign: 'center',
          padding: '24px 16px',
          color: colors.fg.muted,
          fontSize: typography.fontSize.base,
        }}>
          Sin datos suficientes para calcular métricas.
          <br />
          <span style={{ fontSize: typography.fontSize.sm, opacity: 0.7 }}>
            El documento no contiene infraestructuras detectadas.
          </span>
        </div>
      </div>
    );
  }

  // Top 6 tipologías por volumen
  const topTipos = Object.entries(tipologias)
    .sort(([, a], [, b]) => b.total - a.total)
    .slice(0, 6);

  // Fuentes de geocodificación ordenadas
  const fuentes = Object.entries(fuentesGeocodificacion)
    .sort(([, a], [, b]) => b - a);

  return (
    <div style={S.root} data-testid="province-metrics-panel">
      {/* Cabecera: siempre visible, click abre/cierra */}
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: '16px' }}
        onClick={() => { const next = !open; setOpen(next); if (!next) setPulseKey(k => k + 1); }}
      >
        {/* Cabecera con icono alineado */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', lineHeight: 1 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke={colors.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={{ flexShrink: 0, display: 'block' }}>
            <rect x="3" y="3" width="7" height="7"/>
            <rect x="14" y="3" width="7" height="7"/>
            <rect x="3" y="14" width="7" height="7"/>
            <rect x="14" y="14" width="7" height="7"/>
          </svg>
          <span style={{ ...S.title, margin: 0 }}>Geolocalización y calidad</span>
        </div>
        <div
          key={open ? 'open' : `closed-${pulseKey}`}
          className={open ? '' : 'ptel-toggle-pulse'}
          style={{
            width: '32px', height: '32px', display: 'flex', alignItems: 'center',
            justifyContent: 'center', background: colors.bg.secondary,
            border: `1px solid ${colors.border.DEFAULT}`, borderRadius: '8px', flexShrink: 0,
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke={colors.fg.secondary}
            strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
            style={{ width: '14px', height: '14px', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
          >
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </div>
      </div>

      {/* VALMUNI-b2b1 (DOP-2 a): aviso de documento, siempre visible, sin cerrar ni bloquear.
          Mismo texto y aspecto que en P2. */}
      {avisoContorno && (
        <div style={{ marginBottom: '16px' }} data-testid="sin-validar-contorno-p4">
          <InfoBanner variant="valida" visible dismissible={false}>
            <BannerStrong>{PREFIJO_AVISO_CONTORNO.trim()}</BannerStrong>{' '}
            {avisoContorno.startsWith(PREFIJO_AVISO_CONTORNO)
              ? avisoContorno.slice(PREFIJO_AVISO_CONTORNO.length)
              : avisoContorno}
          </InfoBanner>
        </div>
      )}

      {/* KPIs globales: siempre visibles */}
      <div style={S.kpiRow}>
        <div style={S.kpiCard}>
          <div style={{ ...S.kpiValue, color: getCoberturaColor(geoPct) }}>
            {geoPct}%
          </div>
          <div style={S.kpiLabel}>geolocalización</div>
        </div>
        <div style={S.kpiCard}>
          <div style={{ ...S.kpiValue, color: getCoberturaColor(scoreMedio) }}>
            {scoreMedio}
          </div>
          <div style={S.kpiLabel}>score medio</div>
        </div>
        <div style={S.kpiCard}>
          <div style={{ ...S.kpiValue, color: colors.fg.primary }}>
            {resumen.total}
          </div>
          <div style={S.kpiLabel}>infraestructuras</div>
        </div>
      </div>

      {/* Detalle: solo visible al expandir */}
      {open && (
        <>
          {/* Distribución por tipología */}
          {topTipos.length > 0 && (
            <>
              <div style={{ ...S.sectionTitle, marginTop: '16px' }}>Por tipología</div>
              {topTipos.map(([tipo, { total: t, conCoordenadas }]) => {
                const pct = t > 0 ? (conCoordenadas / t) * 100 : 0;
                return (
                  <div key={tipo} style={S.tipRow}>
                    <div style={S.tipName} title={tipo}>{tipo}</div>
                    <div style={S.barTrack}>
                      <div style={{ ...S.barFill, width: `${pct}%`, background: getCoberturaColor(pct) }} />
                    </div>
                    <div style={S.tipPct}>{pct.toFixed(0)}%</div>
                  </div>
                );
              })}
            </>
          )}

          {/* Fuentes de geocodificación */}
          {fuentes.length > 0 && (
            <>
              <div style={S.divider} />
              <div style={S.sectionTitle}>Fuentes de geolocalización utilizadas en este proceso</div>
              <div style={{ ...S.sourceRow, justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' as const }}>
                {fuentes.map(([fuente, count]) => {
                  const sc = getSourceColor(fuente);
                  return (
                    <div key={fuente} data-testid="source-pill" style={{
                      ...S.sourcePill,
                      color: sc.color,
                      background: sc.bg,
                      borderColor: sc.border,
                    }}>
                      {fuente} · {count}
                    </div>
                  );
                })}
                </div>
                <span style={{
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  color: colors.primary,
                  fontWeight: typography.fontWeight.semibold,
                  flexShrink: 0,
                  marginLeft: '16px',
                }}>
                  {fuentes.reduce((s, [, n]) => s + n, 0)} registros
                </span>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};
